import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import React from 'react'
import { Button } from '../../../components/ui/button'

export interface Pagination {
    from: number
    to: number
    total: number
    current: number
    last_page: number
}

interface PaginationProps {
    onClick?: any
    loading?: boolean
    className?: ClassValue
    minimal?: boolean
    total: number
    current: number
    // Small mode:
    // - has smaller buttons
    // - no "jump to start" or "jump to end" buttons (<< or >>)
    // - a much more horizontally narrow list of pages
    small?: boolean
}

export const Pagination = ({
    current,
    total,
    onClick,
    loading,
    className,
    minimal,
    small,
}: PaginationProps): JSX.Element | null => {
    if (!current || !total || total === 1) {
        return null
    }

    const handleOnClick = (page: number) => (): void => {
        if (onClick) {
            onClick(page)
        }
    }

    const pages: number[] = []
    for (let i = 1; i <= total; i++) {
        pages.push(i)
    }

    const renderPages = () => {
        if (small) {
            if (pages.length <= 3) {
                return pages.map((page) => pageButton(page))
            } else {
                return pageButton(current)
            }
        }

        if (pages.length <= 7) {
            return pages.map((page) => pageButton(page))
        } else {
            if (current > 3 && current < pages.length - 3) {
                return [
                    pageButton(1),
                    pageButton(2),
                    <Ellipsis key="..3" />,
                    pageButton(current - 1),
                    pageButton(current),
                    pageButton(current + 1),
                    <Ellipsis key="..-3" />,
                    pageButton(pages.length - 2),
                    pageButton(pages.length - 1),
                ]
            } else if (current <= 3) {
                return [
                    pageButton(1),
                    pageButton(2),
                    pageButton(3),
                    pageButton(4),
                    <Ellipsis key="..-3" />,
                    pageButton(pages.length - 2),
                    pageButton(pages.length - 1),
                ]
            } else {
                return [
                    pageButton(1),
                    pageButton(2),
                    <Ellipsis key="..3" />,
                    pageButton(pages.length - 4),
                    pageButton(pages.length - 3),
                    pageButton(pages.length - 2),
                    pageButton(pages.length - 1),
                ]
            }
        }
    }

    /**
     * A clickable button, showing the passed page number.
     *
     * @param {number} page
     * @return {JSX.Element}
     */
    const pageButton = (page: number) => (
        <Button
            minimal={minimal}
            text={small ? <span className="text-xs">{page}</span> : [page]}
            key={page}
            onClick={handleOnClick(page)}
            disabled={loading || page === current}
        />
    )

    /**
     * A disabled button, showing an ellipsis ("...").
     *
     * @return {JSX.Element}
     */
    const Ellipsis = () => <Button minimal={minimal} text="..." disabled={true} />

    return (
        <div
            key={current}
            className={classNames('flex items-center', className, { 'bp4-skeleton': loading })}
        >
            {!small && (
                <Button
                    minimal={minimal}
                    icon={'double-chevron-left'}
                    disabled={loading || current === 1}
                    onClick={handleOnClick(1)}
                />
            )}
            <Button
                minimal={minimal}
                icon={'chevron-left'}
                disabled={loading || current === 1}
                onClick={handleOnClick(current - 1)}
            />
            {renderPages()}
            <Button
                minimal={minimal}
                icon={'chevron-right'}
                disabled={loading || current === total}
                onClick={handleOnClick(current + 1)}
            />
            {!small && (
                <Button
                    minimal={minimal}
                    icon={'double-chevron-right'}
                    disabled={loading || current === total}
                    onClick={handleOnClick(total)}
                />
            )}
        </div>
    )
}
